/**
 * The figures the import could not vouch for, in one place, correctable.
 *
 * WHY THIS EXISTS, from the measurement rather than from taste. On the nine reference
 * filings' best readings 83 of 92 compared lines tie — and seven of the eight that do not
 * are the two figures v2 DERIVES, cash and net assets, rather than figures it reads. When
 * a reading is good it is very good.
 *
 * The trouble is that one cold read often is not: about a third of figures move between
 * attempts. Selecting the best attempt was measured and does not fix it, because the
 * importer scores self-consistency and a wrong reading can be perfectly self-consistent.
 *
 * So the import stopped trying to be sure and started saying which figures it is not sure
 * of. The flags come from two independent sources and neither alone would do: readings
 * that DISAGREED with each other, and figures a check against the filing's own printed
 * totals could not reconcile. The second catches what the first cannot — payhawk's
 * administration figure is stable across three reads and stably 100,000 short, so
 * agreement plainly does not imply correctness.
 *
 * A LIST RATHER THAN HIGHLIGHTED CELLS, for two reasons. The input views wrap v1's shared
 * table components, and v1's behaviour is not ours to change for a v2 feature. And there
 * are typically a handful of flags spread over nine tabs: a reviewer wants to work through
 * them, not hunt for them.
 *
 * NOT A SCORE. Every entry is a fact — two readings differed, or a total does not add up.
 * Attaching a percentage would invent a precision nothing here supports.
 */

import { useState } from 'react';
import { formatMoney } from '../../../lib/currency';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type { FigureFlag } from '../../../types/financial-model-v2';

/**
 * A dotted input path, as a person would say it.
 *
 * Only the prefixes worth naming; anything else falls back to the path itself, which is
 * ugly but honest — better than a wrong friendly name, and it still says where to look.
 */
const AREAS: [string, string][] = [
  ['revenue.', 'Revenue'],
  ['cogs.', 'COGS'],
  ['staffing.', 'Staffing'],
  ['indirectCosts.', 'Indirect costs'],
  ['fixedAssets.', 'Fixed assets'],
  ['controlAccount.', 'Control account'],
  ['balanceLines.', 'Balance sheet lines'],
  ['pnlLines.', 'P&L lines'],
  ['opening.', 'Opening balances'],
  ['leases.', 'Leases'],
  ['debtSchedule.', 'Debt'],
  ['shares.', 'Shares'],
];

function area(path: string): string {
  const hit = AREAS.find(([prefix]) => path.startsWith(prefix));
  return hit ? hit[1] : 'Model';
}

/** The leaf name, spaced out: `otherAdmin` reads as `other admin`. */
function leaf(path: string): string {
  const last = path.split('.').pop() ?? path;
  return last.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
}

function Row({
  flag,
  currency,
  onApply,
}: {
  flag: FigureFlag;
  currency: string;
  onApply: (value: number) => void;
}) {
  const [typed, setTyped] = useState('');

  // A check-derived flag names a figure the model DERIVES — cash, net assets. Those are
  // not editable inputs, so offering a correction box for one would be offering a control
  // that cannot work. It gets the explanation and nothing else.
  const correctable = flag.source === 'disagreement' && flag.period !== '';

  return (
    <div className="border-b border-gray-100 px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <span className="text-sm font-medium text-gray-900">{leaf(flag.path)}</span>
          <span className="ml-2 text-xs text-gray-500">
            {area(flag.path)}
            {flag.period && ` · ${flag.period.replace('_', ' ')}`}
          </span>
        </div>
        <span
          className={
            flag.source === 'check'
              ? 'shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-900'
              : 'shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-700'
          }
        >
          {flag.source === 'check' ? 'does not add up' : 'readings differed'}
        </span>
      </div>

      <p className="mt-1 text-xs leading-relaxed text-gray-600">{flag.reason}</p>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <span className="text-gray-500">in the model</span>
        <span className="font-medium tabular-nums text-gray-900">
          {formatMoney(flag.value, currency)}
        </span>

        {flag.alternatives.length > 0 && (
          <>
            <span className="text-gray-400">·</span>
            <span className="text-gray-500">
              {flag.source === 'check' ? 'the accounts report' : 'also read as'}
            </span>
            {flag.alternatives.map((value) => (
              // Clickable where it can be applied: the other reading is often visibly
              // the right one, and retyping a figure that is already on the screen is
              // the kind of work software should not ask for.
              <button
                key={value}
                type="button"
                disabled={!correctable}
                onClick={() => onApply(value)}
                className="rounded border border-gray-300 px-1.5 py-0.5 font-medium tabular-nums text-gray-800 enabled:hover:bg-gray-50 disabled:border-transparent disabled:bg-transparent disabled:text-gray-600"
              >
                {formatMoney(value, currency)}
              </button>
            ))}
          </>
        )}
      </div>

      {correctable && (
        <div className="mt-2 flex items-center gap-2">
          <input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            inputMode="decimal"
            placeholder="or type the right figure"
            className="w-44 rounded border border-gray-300 px-2 py-1 text-xs text-right tabular-nums"
          />
          <button
            type="button"
            disabled={typed.trim() === ''}
            onClick={() => {
              const value = Number(typed.replace(/,/g, ''));
              if (!Number.isNaN(value)) onApply(value);
            }}
            className="rounded bg-gray-900 px-2.5 py-1 text-xs text-white disabled:opacity-40"
          >
            Use this
          </button>
        </div>
      )}
    </div>
  );
}

export function ImportReviewPanel() {
  const model = useFinancialModelV2Store((s) => s.model);
  const updateInput = useFinancialModelV2Store((s) => s.updateInput);

  // Cleared locally as each is dealt with. Not written back to the model: a flag is
  // evidence about one particular reading, and the reading is not repeated — so it is
  // worth keeping on the record even after a figure has been corrected. What matters is
  // that the reviewer can see what they have left.
  const [resolved, setResolved] = useState<Set<string>>(new Set());

  const flags = model?.figureFlags ?? [];
  if (!model || flags.length === 0) return null;

  const currency = model.settings.baseCurrency;
  const key = (flag: FigureFlag) => `${flag.path}@${flag.period}`;
  const outstanding = flags.filter((flag) => !resolved.has(key(flag)));

  return (
    <section className="mt-6 max-w-3xl rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Figures worth checking from the import
        </h3>
        <p className="mt-1 text-xs leading-relaxed text-gray-600">
          {outstanding.length === 0 ? (
            <>
              All {flags.length} checked. The import flagged these because two readings of
              the filing differed, or because a figure did not add up against the totals
              the filing itself prints — not because it scored them.
            </>
          ) : (
            <>
              {outstanding.length} of {flags.length} left. These are the figures the import
              could not vouch for: either two readings of the filing differed, or the
              figure is part of a total that does not add up against what the filing
              prints. Everything else read consistently.
            </>
          )}
        </p>
      </div>

      {outstanding.length === 0 ? (
        <p className="px-4 py-3 text-xs text-gray-500">
          Nothing left to review.
        </p>
      ) : (
        <div>
          {outstanding.map((flag) => (
            <Row
              key={key(flag)}
              flag={flag}
              currency={currency}
              onApply={(value) => {
                updateInput(flag.path, flag.period, value);
                setResolved((current) => new Set(current).add(key(flag)));
              }}
            />
          ))}
        </div>
      )}

      {outstanding.length > 0 && (
        <div className="border-t border-gray-100 px-4 py-2">
          <button
            type="button"
            onClick={() =>
              setResolved(new Set(flags.map((flag) => key(flag))))
            }
            className="text-xs text-gray-500 underline hover:text-gray-800"
          >
            Mark all as checked
          </button>
        </div>
      )}
    </section>
  );
}
